import { jsPDF } from 'jspdf';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { logEvent } from '../utilities/ga';
import useStorage from './useStorage';

// Constants for Pokemon card dimensions (in mm)
// Standard Pokemon card size is 63mm x 88mm
const POKEMON_CARD_WIDTH = 63;
const POKEMON_CARD_HEIGHT = 88;

// Maximum cards per PDF to prevent browser crashes
const MAX_CARDS_PER_PDF = 50;

export default function usePokemonPDF() {
  const [downloadStatus, setDownloadStatus] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });
  const { storage } = useStorage();

  // PDF settings
  const [outerPadding, setOuterPadding] = useState(true);
  const [betweenPadding, setBetweenPadding] = useState(false);
  const [format, setFormat] = useState('letter');
  const [scale, setScale] = useState('100%');

  // Load settings from storage
  useEffect(() => {
    const loadSettings = async () => {
      if (!storage) return;

      const storedOuterPadding = await storage.getItem('PDF_Outer_Padding_Pokemon');
      if (storedOuterPadding !== null) setOuterPadding(storedOuterPadding);

      const storedBetweenPadding = await storage.getItem('PDF_Between_Padding_Pokemon');
      if (storedBetweenPadding !== null) setBetweenPadding(storedBetweenPadding);

      const storedFormat = await storage.getItem('PDF_Format_Pokemon');
      if (storedFormat) setFormat(storedFormat);

      const storedScale = await storage.getItem('PDF_Scale_Pokemon');
      if (storedScale) {
        // Ensure scale is always stored as a string with % format
        if (typeof storedScale === 'number') {
          setScale(`${storedScale}%`);
        } else if (typeof storedScale === 'string') {
          // Make sure the string has % at the end
          setScale(storedScale.includes('%') ? storedScale : `${storedScale}%`);
        }
      }
    };

    loadSettings();
  }, [storage]);

  // Save settings to storage when they change
  useEffect(() => {
    const saveSettings = async () => {
      if (!storage) return;
      await storage.setItem('PDF_Outer_Padding_Pokemon', outerPadding);
      await storage.setItem('PDF_Between_Padding_Pokemon', betweenPadding);
      await storage.setItem('PDF_Format_Pokemon', format);
      await storage.setItem('PDF_Scale_Pokemon', scale);
    };

    saveSettings();
  }, [outerPadding, betweenPadding, format, scale, storage]);

  // Calculate card dimensions based on scale
  const getScaleValue = () => {
    if (typeof scale === 'number') return scale;
    if (typeof scale === 'string') {
      const numericValue = parseInt(scale.replace('%', ''));
      return isNaN(numericValue) ? 100 : numericValue;
    }
    return 100; // Default to 100% if scale is invalid
  };

  const scaleValue = getScaleValue();
  const cardWidth = (POKEMON_CARD_WIDTH * scaleValue) / 100;
  const cardHeight = (POKEMON_CARD_HEIGHT * scaleValue) / 100;

  // Calculate rows and columns based on page format and card dimensions
  const getPageDimensions = () => {
    // Page dimensions in mm
    const pageDimensions = {
      a4: { width: 210, height: 297 },
      letter: { width: 215.9, height: 279.4 },
    };

    return pageDimensions[format.toLowerCase()] || pageDimensions.a4;
  };

  const { width: pageWidth, height: pageHeight } = getPageDimensions();

  // Calculate rows and columns based on padding settings
  const paddingBetween = betweenPadding ? 6 : 0;
  const columns = Math.floor(pageWidth / (cardWidth + paddingBetween));
  const rows = Math.floor(pageHeight / (cardHeight + paddingBetween));

  // Convert image URL to data URL
  const toDataUrl = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = (error) => reject(error);
      img.src = url;
    });
  };

  // Process a batch of cards
  const processCardBatch = async (cardBatch, batchIndex, totalBatches) => {
    const images = cardBatch.map((card) => {
      // Handle Pokemon cards
      return {
        quantity: card?.quantity || 1,
        image: card?.details?.image_uris?.large || card?.details?.image_uris?.small,
      };
    });

    // Flatten the array
    const flattenedImages = images.flat();

    const imagesToPrint = [];
    for (const image of flattenedImages) {
      for (let index = 0; index < image.quantity; index++) {
        imagesToPrint.push(image.image);
      }
    }

    const pdf = new jsPDF({
      unit: 'mm',
      format: format.toLowerCase(),
    });
    const paddingOuter = outerPadding ? 6 : 0;
    const paddingBetween = betweenPadding ? 6 : 0;
    const imagesPerPage = rows * columns;

    const pagesNeeded = Math.ceil(imagesToPrint.length / imagesPerPage);

    let u = 0;

    for (let p = 0; p < pagesNeeded; p++) {
      if (p > 0) pdf.addPage();
      for (let j = 0; j < rows; j++) {
        for (let i = 0; i < columns; i++) {
          if (u < imagesToPrint.length) {
            try {
              const proxyUrl = `/api/image-proxy?url=${encodeURIComponent(imagesToPrint[u])}`;
              const image = await toDataUrl(proxyUrl);
              const x = i * (cardWidth + paddingBetween) + paddingOuter;
              const y = j * (cardHeight + paddingBetween) + paddingOuter;
              pdf.addImage(image, 'PNG', x, y, cardWidth, cardHeight);
              u++;
            } catch (error) {
              u++; // Increment counter even when image fails to load
            }
          }
        }
      }
    }

    const filename = totalBatches > 1 ? `tcg-stacked-pokemon-proxies-part-${batchIndex + 1}-of-${totalBatches}.pdf` : 'tcg-stacked-pokemon-proxies.pdf';

    await pdf.save(filename, {
      returnPromise: true,
    });
  };

  // Download PDF
  const downloadPDF = async (cards) => {
    setDownloadStatus('pending');

    logEvent({
      action: 'startDownloadPokemonPDF',
      params: {
        scale,
        outerPadding,
        betweenPadding,
        format,
      },
    });

    try {
      // Filter out only Pokemon cards
      const pokemonCards = cards.filter((card) => card?.details?.tcg === 'pokemon');

      if (pokemonCards.length === 0) {
        toast.error('No Pokemon cards found in the print list');
        setDownloadStatus('error');
        return;
      }

      // Split cards into batches of MAX_CARDS_PER_PDF
      const cardBatches = [];
      for (let i = 0; i < pokemonCards.length; i += MAX_CARDS_PER_PDF) {
        cardBatches.push(pokemonCards.slice(i, i + MAX_CARDS_PER_PDF));
      }

      setDownloadProgress({ current: 0, total: cardBatches.length });

      // Process each batch
      for (let i = 0; i < cardBatches.length; i++) {
        setDownloadProgress({ current: i + 1, total: cardBatches.length });

        if (i > 0 && cardBatches.length > 1) {
          toast.info(`Generating Pokemon PDF ${i + 1} of ${cardBatches.length}...`);
        }

        await processCardBatch(cardBatches[i], i, cardBatches.length);
      }

      if (cardBatches.length > 1) {
        toast.success(`Successfully generated ${cardBatches.length} Pokemon PDF files`);
      } else {
        toast.success('Pokemon PDF generated successfully');
      }

      logEvent({
        action: 'successDownloadPokemonPDF',
        params: {
          scale,
          outerPadding,
          betweenPadding,
          format,
          batches: cardBatches.length,
        },
      });
      setDownloadStatus('success');
    } catch (error) {
      console.error('Error downloading Pokemon PDF:', error);
      toast.error(`Unexpected error. Try again later.`);

      logEvent({
        action: 'errorDownloadPokemonPDF',
        params: {
          error,
        },
      });
      setDownloadStatus('error');
      throw error;
    }
  };

  return {
    downloadPDF,
    downloadStatus,
    downloadProgress,
    outerPadding,
    setOuterPadding,
    betweenPadding,
    setBetweenPadding,
    format,
    setFormat,
    scale,
    setScale,
  };
}
